import React from 'react';
import { ArrowRight, FileText, KeyRound, Package2, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SubscriptionPlansRBAC from '../components/superadmin/SubscriptionPlansRBAC';
import SuperadminPlanEditPage from './SuperadminPlanEditPage';
import SuperadminPlanOrdersPage from './SuperadminPlanOrdersPage';

const SuperadminPlansPage = ({ activeTab, onNavigate }) => {
  if (activeTab === 'plans/create') {
    return (
      <SuperadminPlanEditPage
        mode="create"
        onBack={() => onNavigate?.('plans')}
      />
    );
  }

  if (activeTab?.startsWith('plans/edit/')) {
    const planId = activeTab.replace('plans/edit/', '');
    return (
      <SuperadminPlanEditPage
        planId={planId}
        onBack={() => onNavigate?.('plans')}
      />
    );
  }

  if (activeTab === 'plans/subscription-plans') {
    return (
      <SubscriptionPlansRBAC
        onCreatePlan={() => onNavigate?.('plans/create')}
        onEditPlan={(planId) => onNavigate?.(`plans/edit/${planId}`)}
      />
    );
  }

  if (activeTab === 'plans/order-details') {
    return <SuperadminPlanOrdersPage onNavigate={onNavigate} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Subscription Plans</h2>
          <p className="text-sm text-muted-foreground">
            Manage subscription plans, pricing, and order visibility across all tenants.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => onNavigate?.('plans/subscription-plans')}>
            <Package2 className="mr-2 h-4 w-4" />
            Open Subscription Plans
          </Button>
          <Button variant="outline" onClick={() => onNavigate?.('plans/order-details')}>
            <FileText className="mr-2 h-4 w-4" />
            Plan Order Details
          </Button>
          <Button variant="outline" onClick={() => onNavigate?.('plans/create')}>
            <KeyRound className="mr-2 h-4 w-4" />
            Create Plan
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-primary/20">
          <CardHeader className="space-y-2">
            <Badge variant="outline" className="w-fit">Recommended</Badge>
            <CardTitle className="flex items-center gap-2">
              <Package2 className="h-5 w-5" />
              Subscription Plans
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Create, edit, activate, and assign plans with module entitlements and access rules in one place.
            </p>
            <Button className="w-full justify-between" onClick={() => onNavigate?.('plans/subscription-plans')}>
              Manage Plans
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Plan Order Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Review plan purchases, payment receipts, approval status, and order history in a dedicated list view.
            </p>
            <Button variant="outline" className="w-full justify-between" onClick={() => onNavigate?.('plans/order-details')}>
              Open Plan Orders
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Review users, assign roles, and keep tenant-level access easier to audit and maintain.
            </p>
            <Button variant="outline" className="w-full justify-between" onClick={() => onNavigate?.('users')}>
              Open Users
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>Use this area to keep subscription setup simple for superadmin teams.</p>
            <Button variant="outline" className="w-full justify-between" onClick={() => onNavigate?.('plans/create')}>
              Add New Subscription Plan
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => onNavigate?.('plans/order-details')}>
              Review Plan Orders
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperadminPlansPage;
